import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Copy, KeyRound, MoreHorizontal, Plus, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar } from "@/components/Avatar";
import { useChat } from "@/lib/chat";
import { api, errMsg } from "@/lib/api";
import { copyText, fmtRelative } from "@/lib/format";
import { analytics } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`;

const TokenDialog = ({ data, onClose }) => (
  <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="tc-dialog" style={{ maxWidth: 560 }} data-testid="bot-token-dialog">
      <DialogTitle>{data?.rotated ? "New token for" : "Token for"} {data?.bot?.display_name}</DialogTitle>
      <DialogDescription>Copy it now — for security this is the only time it's shown. The bot sends it as <code className="tc-code">Authorization: Bearer …</code>{data?.rotated ? " The previous token stopped working immediately." : ""}</DialogDescription>
      <div className="tc-token-reveal" data-testid="bot-token-value"><code>{data?.token}</code><button className="tc-btn tc-btn--primary sm" onClick={() => { copyText(data.token); toast.success("Token copied"); }} data-testid="bot-token-copy"><Copy size={13} /> Copy</button></div>
      <pre className="tc-codeblock" style={{ marginTop: 12 }}>{`curl -H "Authorization: Bearer ${data?.token}" ${API}/bot/me`}</pre>
      <div className="tc-dialog__footer"><button className="tc-btn tc-btn--primary" onClick={onClose} data-testid="bot-token-done">I've saved it</button></div>
    </DialogContent>
  </Dialog>
);

const CreateDialog = ({ open, onClose, onCreated, roles }) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [role, setRole] = useState("bot");
  const [channels, setChannels] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setDesc(""); setRole("bot"); setPicked([]); api.get("/conversations/browse").then((r) => setChannels(r.data.filter((c) => !c.is_default))).catch(() => {}); } }, [open]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bots", { name, description: desc, role, channels: picked });
      analytics.track("admin_bot_created", { category: "admin", target: "bot", target_id: data.bot.id, metadata: { role, channels: picked.length } });
      onCreated(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="tc-dialog" style={{ maxWidth: 520 }} data-testid="bot-create-dialog">
        <DialogTitle>New bot</DialogTitle>
        <DialogDescription>A bot is a member account driven by code — a Grok agent, a script, an integration. It gets its own token, email address and permissions.</DialogDescription>
        <label className="tc-label">Name</label>
        <input className="tc-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Grok Assistant" data-testid="bot-name" autoFocus />
        <label className="tc-label" style={{ marginTop: 12 }}>What it does <span className="muted">(shown on its profile)</span></label>
        <input className="tc-input" value={desc} onChange={(e) => setDesc(e.target.value.slice(0, 300))} placeholder="Answers policy questions when mentioned" data-testid="bot-description" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
          <div>
            <label className="tc-label">Role</label>
            <select className="tc-select" style={{ width: "100%" }} value={role} onChange={(e) => setRole(e.target.value)} data-testid="bot-role">
              {Object.keys(roles || { bot: 1, member: 1, guest: 1 }).map((r) => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}
            </select>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{role === "bot" ? "Post, reply in threads, react, upload, DM. No channel creation, huddles or @channel." : "Fine-tune later in Permissions."}</div>
          </div>
          <div>
            <label className="tc-label">Channels <span className="muted">(besides defaults)</span></label>
            <div className="tc-checklist" data-testid="bot-channels">
              {channels.map((c) => <label key={c.id} className="tc-check"><input type="checkbox" checked={picked.includes(c.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))} /> #{c.name}</label>)}
              {!channels.length && <span className="muted" style={{ fontSize: 12 }}>Default channels only</span>}
            </div>
          </div>
        </div>
        <div className="tc-dialog__footer">
          <button className="tc-btn tc-btn--outline" onClick={onClose}>Cancel</button>
          <button className="tc-btn tc-btn--primary" disabled={name.trim().length < 2 || busy} onClick={create} data-testid="bot-create-submit"><Plus size={14} /> {busy ? "Creating…" : "Create bot"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QuickStart = ({ bot }) => {
  const handle = bot?.handle || "grok-assistant";
  const [tab, setTab] = useState("python");
  const py = `# pip install websockets requests openai   (xAI's API is OpenAI-compatible)
import json, os, requests, websockets, asyncio
from openai import OpenAI

TC = "${API}"
TOKEN = os.environ["TOTAL_CHAT_BOT_TOKEN"]          # tcb_… from Admin › Bots
grok = OpenAI(api_key=os.environ["XAI_API_KEY"], base_url="https://api.x.ai/v1")
H = {"Authorization": f"Bearer {TOKEN}"}
me = requests.get(f"{TC}/bot/me", headers=H).json()  # id, handle, permissions, conversations

def reply(conversation_id, text, parent_id=None):
    requests.post(f"{TC}/conversations/{conversation_id}/messages", headers=H,
                  json={"text": text, "parent_id": parent_id, "client_id": os.urandom(8).hex()})

async def main():
    async with websockets.connect("${WS}?token=" + TOKEN + "&client=bot") as ws:
        async for raw in ws:
            ev = json.loads(raw)
            if ev.get("type") != "message.new": continue
            m = ev["message"]
            if m["user_id"] == me["id"] or me["id"] not in m.get("mentions", []): continue   # only when @${handle} is mentioned
            answer = grok.chat.completions.create(model="grok-4", messages=[{"role": "user", "content": m["text"]}]).choices[0].message.content
            reply(m["conversation_id"], answer, parent_id=m.get("parent_id") or m["id"])      # answer in a thread

asyncio.run(main())`;
  const poll = `# No WebSocket? Poll for new mentions every few seconds instead:
r = requests.get(f"{TC}/bot/messages", headers=H, params={"since": cursor, "mentions_only": "true"}).json()
for m in r["messages"]: ...
cursor = r["cursor"]`;
  const curl = `# Who am I / which conversations can I see
curl -H "Authorization: Bearer $TOKEN" ${API}/bot/me

# Post a message (client_id makes retries safe — the same client_id is never posted twice)
curl -X POST -H "Authorization: Bearer $TOKEN" -H "Content-Type: application/json" \\
  -d '{"text":"Hello from ${handle} :wave:","client_id":"run-42"}' ${API}/conversations/<conversation_id>/messages

# Reply in a thread                        -d '{"text":"…","parent_id":"<message_id>"}'
# React                                    POST ${API}/messages/<message_id>/reactions  {"name":"white_check_mark"}
# Read history                             GET  ${API}/conversations/<conversation_id>/messages?limit=50
# New messages since a time (polling)      GET  ${API}/bot/messages?since=2026-06-01T00:00:00Z&mentions_only=true
# Start a DM with a person                 POST ${API}/conversations {"type":"dm","member_ids":["<user_id>"]}
# Realtime events                          wss  ${WS}?token=$TOKEN&client=bot   (message.new, message.updated, reaction …)`;
  return (
    <div className="tc-admin-card" data-testid="bot-quickstart">
      <div className="tc-admin-card__head"><Bot size={16} /> Connect a Grok agent <span className="right"><button className={`tc-tab${tab === "python" ? " is-active" : ""}`} onClick={() => setTab("python")}>Python</button><button className={`tc-tab${tab === "curl" ? " is-active" : ""}`} onClick={() => setTab("curl")}>REST</button></span></div>
      <div className="tc-admin-card__body">
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>Bots use the same API as the app, authenticated with their token. Every action is tracked in analytics under the bot's name and governed by its permissions — a bot can't do anything a member with the same permissions couldn't. Limits: 120 messages / 10 s per bot, 60 reactions / 10 s; over the limit you get <code className="tc-code">429</code> with a <code className="tc-code">Retry-After</code> header. Always send a unique <code className="tc-code">client_id</code> so retries never double-post.</p>
        <pre className="tc-codeblock" style={{ maxHeight: 420, overflow: "auto" }}>{tab === "python" ? `${py}\n\n${poll}` : curl}</pre>
      </div>
    </div>
  );
};

export const Bots = () => {
  const { meId, me } = useChat();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState(null);
  const load = () => api.get("/admin/bots").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const bots = useMemo(() => data?.bots || [], [data]);
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const rotate = (b) => window.confirm(`Rotate the token for ${b.display_name}? The current token stops working immediately.`) && act(async () => { const { data: d } = await api.post(`/admin/bots/${b.id}/rotate`); setToken({ ...d, rotated: true }); analytics.track("admin_bot_token_rotated", { category: "admin", target: "bot", target_id: b.id }); });
  const toggle = (b) => act(() => api.patch(`/admin/bots/${b.id}`, { is_active: b.is_active === false }), b.is_active === false ? `${b.display_name} reactivated` : `${b.display_name} deactivated — its token no longer works`);
  const revoke = (b) => window.confirm(`Revoke ${b.display_name} entirely? Its token is deleted and it is deactivated. Its messages stay.`) && act(() => api.delete(`/admin/bots/${b.id}`), "Bot revoked");
  return (
    <div data-testid="admin-bots">
      <h1>Bots</h1>
      <p className="lead">Give Grok agents and other programs their own seat in Total Chat. Each bot is a member with a token instead of a login, an email address, and permissions you control.</p>
      <div className="tc-admin__toolbar">
        <span className="muted" style={{ fontSize: 13 }}>{bots.length} bot{bots.length === 1 ? "" : "s"} · {bots.filter((b) => b.is_active !== false).length} active</span>
        <span className="grow" />
        {me?.is_admin && <button className="tc-btn tc-btn--outline sm" onClick={() => navigate("/admin/permissions")} data-testid="bots-permissions-button"><ShieldCheck size={14} /> Permissions</button>}
        <button className="tc-btn tc-btn--primary sm" onClick={() => setCreating(true)} data-testid="bots-create-button"><Plus size={14} /> New bot</button>
      </div>
      <div className="tc-admin-card">
        <div style={{ overflowX: "auto" }}>
          <table className="tc-table" data-testid="bots-table">
            <thead><tr><th>Bot</th><th>Role</th><th>Channels</th><th>Token</th><th>Last used</th><th>Owner</th><th>Status</th><th /></tr></thead>
            <tbody>
              {bots.map((b) => (
                <tr key={b.id} data-testid={`bot-row-${b.handle}`} className={b.is_active === false ? "is-dim" : ""}>
                  <td><div className="usercell"><Avatar user={b} size={28} radius={6} /><div><div>{b.display_name} <span className="tc-msg__app">Bot</span></div><div className="sub mono">@{b.handle} · {b.chat_email}</div>{b.description && <div className="sub">{b.description}</div>}</div></div></td>
                  <td><span className="tc-pill">{b.role[0].toUpperCase() + b.role.slice(1)}</span></td>
                  <td>{b.channels}</td>
                  <td className="mono muted" style={{ whiteSpace: "nowrap" }}>{b.has_token ? `tcb_…${b.token_hint}` : "revoked"}</td>
                  <td className="muted" style={{ whiteSpace: "nowrap" }}>{b.last_used_at ? fmtRelative(b.last_used_at) : "never"}</td>
                  <td className="muted">{b.owner?.display_name || "—"}{b.owner?.id === meId ? " (you)" : ""}</td>
                  <td>{b.is_active === false ? <span className="tc-pill danger">Inactive</span> : <span className="tc-pill client">Active</span>}</td>
                  <td style={{ textAlign: "right" }}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="tc-iconbtn" data-testid={`bot-actions-${b.handle}`}><MoreHorizontal size={16} /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="tc-menu">
                        {b.has_token && <DropdownMenuItem className="tc-menu__item" onClick={() => rotate(b)} data-testid={`bot-rotate-${b.handle}`}><KeyRound size={14} /> Rotate token…</DropdownMenuItem>}
                        {me?.is_admin && <DropdownMenuItem className="tc-menu__item" onClick={() => navigate(`/admin/permissions?user=${b.id}`)} data-testid={`bot-permissions-${b.handle}`}>Permissions…</DropdownMenuItem>}
                        <DropdownMenuItem className="tc-menu__item" onClick={() => navigate(`/admin/events?user_id=${b.id}`)}>View activity</DropdownMenuItem>
                        <DropdownMenuItem className="tc-menu__item" onClick={() => toggle(b)} data-testid={`bot-toggle-${b.handle}`}>{b.is_active === false ? "Reactivate" : "Deactivate"}</DropdownMenuItem>
                        {me?.is_admin && b.has_token && <DropdownMenuItem className="tc-menu__item is-danger" onClick={() => revoke(b)} data-testid={`bot-revoke-${b.handle}`}>Revoke token & deactivate</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {data && !bots.length && <tr><td colSpan={8} className="muted" style={{ textAlign: "center", padding: 28 }}>No bots yet. Create one to get a token, then connect your Grok agent with the snippet below.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <QuickStart bot={bots[0]} />
      <CreateDialog open={creating} onClose={() => setCreating(false)} roles={data?.roles} onCreated={(d) => { setCreating(false); setToken(d); load(); }} />
      <TokenDialog data={token} onClose={() => setToken(null)} />
    </div>
  );
};

export default Bots;
